import { useQuery } from 'react-query';
import request from 'app/api/request';

export type ValidationStatus = 'passed' | 'passed-with-warnings' | 'failed';
export type CategoryResult = ValidationStatus | 'not_applicable';
export type Severity = 'error' | 'warning';

export interface TagLocation {
  factId: string;
  pageId: string;
  sectionSuperId: string;
  deletedAt?: string | null;
}

export interface ValidationFinding {
  code: string;
  severity: Severity;
  type: string;
  message: string;
  tag: string | null;
  factIds: string[];
  affectedCount: number;
  factDetails?: Record<string, Record<string, string>>;
  locations?: TagLocation[];
}

export interface ValidationCategory {
  id: 'calculation' | 'ixbrl-validity' | 'xbrl-conformance' | 'esef' | 'business-rules';
  name: string;
  state?: 'checked' | 'not_applicable';
  result: CategoryResult;
  summary: { errors: number; warnings: number };
  findings: ValidationFinding[];
}

export interface ValidationResult {
  status: ValidationStatus;
  ranAt: string;
  durationMs: number;
  categories: ValidationCategory[];
}

export interface XbrlValidationRunPlain {
  id: string;
  documentSuperId: number;
  documentRevisionId: string;
  taxonomyVersion: string;
  status: ValidationStatus;
  ranAt: string;
  durationMs: number;
  triggeredByUserId: number;
  result: ValidationResult;
  createdAt: string;
}

export interface LatestValidation {
  latest: XbrlValidationRunPlain | null;
  stale: boolean;
}

export const validationQueryKeys = {
  latest: (documentSuperId: string | number) => ['validation', 'latest', String(documentSuperId)] as const,
};

export function useLatestValidation(documentSuperId: string | number | undefined) {
  return useQuery<LatestValidation>(
    validationQueryKeys.latest(documentSuperId ?? ''),
    async () => {
      const res = await request.get(`/gaby/validation/${documentSuperId}/latest`);
      return res.data as LatestValidation;
    },
    {
      enabled: documentSuperId !== undefined && documentSuperId !== '',
    }
  );
}
